import get from 'lodash/get';
import { siteId } from '@/utils';
import { getErrorMessage } from '@/store/alerts/mutations.js';

export default {
	fetchProperties({ state, rootGetters, commit, dispatch }, accountId) {
		const fetchId = 'properties-' + accountId;

		return new Promise((resolve, reject) => {
			if (!rootGetters.connected) {
				return resolve();
			}

			if (rootGetters.isFetched(fetchId) || rootGetters.isFetching(fetchId)) {
				return resolve();
			}

			commit('fetching', fetchId, { root: true });

			return this._vm.$api
				.get(`analytics/${accountId}/properties/all`)
				.then(({ body }) => {
					const profiles = { ...state.analyticsProfiles };
					profiles[accountId] = [];

					Object.keys(body).forEach((id) => {
						const profile = body[id];
						if (String(profile.id) === String(accountId)) {
							profiles[accountId].push({
								id: `${profile.gaId}:${profile.uaid}`,
								name: `${profile.name} (${profile.uaid})`,
							});
						}
					});

					commit('setAnalyticsProfiles', profiles);
					commit('fetched', fetchId, { root: true });

					resolve();
				})
				.catch((error) => {
					const fixUrl = this._vm.$urls.app(`integrations/#error-provider-account:analytics:${accountId}`);
					let account = get(
						state.analyticsAccounts.find((a) => a.id === accountId),
						'name',
						accountId
					);
					account = `<a href="${fixUrl}" target="_blank">${account}</a>`;
					error.explanation = `Something went wrong while fetching account properties for <strong>${account}</strong>.`;
					commit('setError', error, { root: true });

					const message = getErrorMessage(error);

					const notice = {
						type: 'action',
						title: 'There was an error fetching analytics properties',
						content: message,
						btns: {
							main: {
								text: 'Fix Now',
								url: fixUrl,
							},
						},
					};
					dispatch('notifications/maybeCreate', notice, { root: true });

					const profiles = { ...state.analyticsProfiles };
					profiles[accountId] = { error: message };
					commit('setAnalyticsProfiles', profiles);

					commit('fetched', fetchId, { root: true });

					reject(error);
					// eslint-disable-next-line no-console
					console.error({ error });
				});
		});
	},
	fetchAnalytics({ rootGetters, commit }) {
		commit('fetching', 'siteAnalytics', { root: true });
		return new Promise((resolve, reject) => {
			if (!rootGetters.connected || !siteId) {
				commit('fetched', 'siteAnalytics', { root: true });
				return resolve();
			}

			return this._vm.$api
				.get(`sites/${siteId}/analytics-accounts`)
				.then(({ body }) => {
					commit('setAnalyticsAccounts', body.accounts);
					commit('setAnalyticsProfiles', body.profiles);

					commit('fetched', 'siteAnalytics', { root: true });
					resolve();
				})
				.catch((error) => {
					commit('setError', error, { root: true });
					commit('doneFetching', 'siteAnalytics', { root: true });
					reject(error);
					// eslint-disable-next-line no-console
					console.error({ error });
				});
		});
	},
	fetchSiteSettings({ rootGetters, commit, dispatch }) {
		commit('fetching', 'siteSettings', { root: true });
		return new Promise((resolve, reject) => {
			if (!rootGetters.connected || !siteId) {
				commit('fetched', 'siteSettings', { root: true });
				return resolve();
			}

			return this._vm.$api
				.get(`sites/${siteId}/settings`)
				.then(({ body }) => {
					commit('setSiteSettings', body.settings);
					commit('setIntegrations', body.integrations);
					commit('setProviders', body.providers);

					dispatch('fetchAnalytics');

					commit('fetched', 'siteSettings', { root: true });
					resolve();
				})
				.catch((error) => {
					commit('setError', error, { root: true });
					commit('doneFetching', 'siteSettings', { root: true });
					reject(error);
					// eslint-disable-next-line no-console
					console.error({ error });
				});
		});
	},
	saveSiteSettings({ rootGetters, commit }, settings) {
		commit('loading', 'siteSettingsSave', { root: true });
		return new Promise((resolve, reject) => {
			if (!rootGetters.connected || !siteId) {
				commit('doneLoading', 'siteSettingsSave', { root: true });
				return resolve();
			}

			return this._vm.$api
				.put(`sites/${siteId}`)
				.send({ settings, from_wp: 1 })
				.then(({ body }) => {
					Object.keys(settings).forEach((s) => {
						settings[s] = get(body, `settings.${s}`, settings[s]);
					});

					commit('updateSiteSettings', settings);
					commit('fetched', 'siteSettings', { root: true });
					commit('doneLoading', 'siteSettingsSave', { root: true });
					resolve();
				})
				.catch((error) => {
					error.explanation = 'Something went wrong while saving the site settings.';
					commit('setError', error, { root: true });
					commit('doneLoading', 'siteSettingsSave', { root: true });
					reject(error);
					// eslint-disable-next-line no-console
					console.error({ error });
				});
		});
	},
	fetchMiscSettings({ commit }) {
		commit('fetching', 'miscSettings', { root: true });
		return new Promise((resolve, reject) => {
			return this._vm.$api
				.get({ wpEndpoint: 'omapp/v1/settings' })
				.then(({ body }) => {
					commit('updateMiscSettings', body);
					commit('fetched', 'miscSettings', { root: true });
					resolve();
				})
				.catch((error) => {
					commit('setError', error, { root: true });
					commit('doneFetching', 'miscSettings', { root: true });
					reject(error);
					// eslint-disable-next-line no-console
					console.error({ error });
				});
		});
	},
	saveMiscSettings({ commit }, settings) {
		commit('loading', 'miscSettingsSave', { root: true });
		return new Promise((resolve, reject) => {
			return this._vm.$api
				.post({ wpEndpoint: 'omapp/v1/settings' })
				.send({ settings })
				.then(({ body }) => {
					commit('updateMiscSettings', body);
					commit('fetched', 'miscSettings', { root: true });
					commit('doneLoading', 'miscSettingsSave', { root: true });
					resolve();
				})
				.catch((error) => {
					commit('setError', error, { root: true });
					commit('doneLoading', 'miscSettingsSave', { root: true });
					reject(error);
					// eslint-disable-next-line no-console
					console.error({ error });
				});
		});
	},
};
